/**
 * ZenMarkup Privacy Analytics — Animation utility module.
 * Shared requestAnimationFrame runner with easing functions.
 * Used by line-chart, pie-chart, and bar chart modules.
 */

/**
 * Ease-out cubic: fast start, smooth deceleration.
 *
 * @param {number} t Progress 0–1.
 * @return {number} Eased value 0–1.
 */
export function easeOutCubic(t) {
	return 1 - Math.pow(1 - t, 3);
}

/**
 * Ease-out quint: faster start, much slower finish.
 *
 * @param {number} t Progress 0–1.
 * @return {number} Eased value 0–1.
 */
export function easeOutQuint(t) {
	return 1 - Math.pow(1 - t, 5);
}

/**
 * Run a timed animation loop via requestAnimationFrame.
 *
 * @param {Function} callback Called each frame with eased progress (0–1).
 * @param {number}   duration Animation duration in ms.
 * @param {Function} [easing] Easing function (default: easeOutCubic).
 * @return {{ cancel: Function }} Handle to cancel the animation.
 */
export function run(callback, duration, easing) {
	const easeFn = easing || easeOutCubic;
	let id = null;
	const start = performance.now();

	function frame(now) {
		const t = Math.min((now - start) / duration, 1);
		callback(easeFn(t));
		if (t < 1) {
			id = requestAnimationFrame(frame);
		} else {
			id = null;
		}
	}

	id = requestAnimationFrame(frame);

	return {
		cancel: function () {
			if (id) {
				cancelAnimationFrame(id);
				id = null;
			}
		}
	};
}
